import { ApiError } from "@kundeninfo/api-client";
import { useKopffaltung } from "../Kopffaltung.js";
import type { Kopffaltung } from "../Kopffaltung.js";
import { kopffeldId } from "../vorgangsdetail.js";
import type { KopffeldTabelle } from "../vorgangsdetail.js";

/*
 * Die Faltungen JE ABSCHNITT einer Vorgangsmaske (Spec „Anwendung auf die drei Vorgangsdialoge“): Parteien, Laufzeit und
 * Zahlplan sind eigene Abschnitte mit eigener Fehlerzahl. Damit das Auto-Aufklappen den RICHTIGEN Abschnitt öffnet, teilt
 * die Maske ihre Kopffeldtabelle nach Abschnitt auf (`SectionFields`); ein Serverfeld steht in genau einem Abschnitt.
 */

/** Je Abschnitt eine Faltung (der Zustand gehört der Maske) und der gemeinsame Rückweg auf den Start. */
export type SectionFolds<K extends string> = Record<K, Kopffaltung> & { resetAll(): void };

/** Je Abschnitt: Serverfeld → Endung der Feld-Kennung (Bauart von `KopffeldTabelle`). */
export type SectionFields<K extends string> = Readonly<Record<K, KopffeldTabelle>>;

/**
 * Eine Faltung je Schlüssel von `start` (offen/zu beim Start und nach `resetAll`). Die SCHLÜSSEL sind fest — je
 * Maske eine Tabelle —, darum ruft die Schleife in jeder Zeichnung dieselben Hooks in derselben Folge; die WERTE dürfen sich
 * ändern (Notiz „offen, wenn Text da ist“).
 */
export function useSectionFolds<K extends string>(start: Readonly<Record<K, boolean>>): SectionFolds<K> {
  const keys = Object.keys(start) as K[];
  const folds = {} as Record<K, Kopffaltung>;
  for (const k of keys) folds[k] = useKopffaltung(start[k]);
  return { ...folds, resetAll: () => { for (const k of keys) folds[k].zuruecksetzen(); } };
}

/** Der Abschnitt, in dem dieses Serverfeld steht, sonst `null`. */
export function sectionOfField<K extends string>(tables: SectionFields<K>, serverField: string): K | null {
  return (Object.keys(tables) as K[]).find((k) => tables[k][serverField] !== undefined) ?? null;
}

/** Den Abschnitt des Feldes aufklappen und das Feld nach dem Zeichnen fokussieren; ein Feld außerhalb lässt alles zu. */
export function showFieldIn<K extends string>(
  f: SectionFolds<K>, tables: SectionFields<K>, prefix: string, serverField: string,
): void {
  const k = sectionOfField(tables, serverField);
  const id = k === null ? null : kopffeldId(tables[k], prefix, serverField);
  if (k !== null && id !== null) f[k].zeigeFeld(id);
}

/**
 * Wie `showFieldIn` für den ERSTEN Befund eines Serverfehlers, der in einem Abschnitt steht. Nimmt `unknown`: Der Aufrufer
 * steht in einem `catch`, und ein Netzabbruch trägt keine Feldbefunde — dann geschieht nichts.
 */
export function showFirstErrorIn<K extends string>(
  f: SectionFolds<K>, tables: SectionFields<K>, prefix: string, error: unknown,
): void {
  if (!(error instanceof ApiError)) return;
  const finding = (error.fields ?? []).find((b) => sectionOfField(tables, b.field) !== null);
  if (finding !== undefined) showFieldIn(f, tables, prefix, finding.field);
}

/** Wie viele Felder je Abschnitt einen Befund tragen — der Fehlerzähler im Kopf, auch zugeklappt sichtbar. */
export function errorsPerSection<K extends string>(
  tables: SectionFields<K>, errorsPerField?: Readonly<Record<string, string>>,
): Record<K, number> {
  const fields = Object.keys(errorsPerField ?? {});
  const count = {} as Record<K, number>;
  for (const k of Object.keys(tables) as K[]) count[k] = fields.filter((field) => tables[k][field] !== undefined).length;
  return count;
}
